import { useState, FormEvent, useMemo, useEffect } from "react";
import Form from "react-bootstrap/Form";
import { ButtonWithSpinner } from "../../common/button-with-spinner/button-with-spinner";
import { resources } from "@constants/hardcoded-strings";
import { Company, Alert } from "@idw-types/data/reference-data/generic";
import { SelectOption } from "@idw-types/common";
import { MultiSelectFormGroup } from "../../common/multi-select-form-group/multi-select-form-group";
import { SearchFormFilterRow } from "../../common/search-form-filter-row/search-form-filter-row";
import { TimeRangeSearchFormFilterRow } from "../../common/time-range-search-form-filter-row/time-range-search-form-filter-row";
import { sortStrings } from "@utils/string-util";
import { LabelWidthTypes } from "@enums/label-width-types";
import { LayoutTypes } from "@enums/layout-types";
import { ButtonTypes } from "@enums/button-types";
import { getTimeRange, getTimeRangeLabel } from "@utils/date-util";
import { TimeRangeTypes } from "@enums/time-range-types";
import { MONTH_DAY_YEAR_INPUT_DATE_FORMAT } from "@constants/date-formats";
import { AlertEventsSearchFiltersState } from "@idw-types/state/alert-events-state";
import { mapEntitiesWithNameAndIdToSelectOptions, mapSelectOptionsToValues } from "@utils/select-option-mapping-util";
import { initialAlertEventsState } from "@redux/reducers/alert-events-reducer";
import Button from "react-bootstrap/Button";
import styles from "./alert-events-search-form.less";
import { SearchFormHeaderContainer } from "../../common/search-form-header-container/search-form-header-container";

type AlertEventsSearchFormProps = {
    handleSearch: (searchFilters: AlertEventsSearchFiltersState) => void;
    searchFilters: AlertEventsSearchFiltersState;
    alerts: Array<Alert>;
    companies: Array<Company>;
    requestInProgress: boolean;
};

type AlertEventsSearchFormState = {
    selectedCompanyOptions: Array<SelectOption>;
    selectedAlertOptions: Array<SelectOption>;
    selectedTimeRange: SelectOption;
    startDate: Date;
    endDate: Date;
};

export const AlertEventsSearchForm = (props: AlertEventsSearchFormProps) => {
    const maxDate = useMemo(() => new Date(), []);
    const minDate = useMemo(() => new Date(2015, 0, 1), []);

    const [labelWidth] = useState(LabelWidthTypes.EXTRA_SMALL);
    const [layout] = useState(LayoutTypes.COLUMN);

    const {
        searchFilters,
        requestInProgress,
        alerts,
        companies,
        handleSearch
    } = props;

    const getFilterStateFromSearchFilters = (searchFilters: AlertEventsSearchFiltersState): AlertEventsSearchFormState => {
        let selectedCompanies = companies.filter(company => searchFilters.companyIds.includes(company.id));

        if (companies.length === 1) {
            selectedCompanies = companies;
        }

        const selectedAlerts = alerts.filter(alert => searchFilters.alertIds.includes(alert.id));

        return {
            selectedCompanyOptions: mapEntitiesWithNameAndIdToSelectOptions(selectedCompanies),
            selectedAlertOptions: mapEntitiesWithNameAndIdToSelectOptions(selectedAlerts),
            selectedTimeRange: {
                label: getTimeRangeLabel(searchFilters.timeRangeType),
                value: searchFilters.timeRangeType
            },
            startDate: searchFilters.startDate,
            endDate: searchFilters.endDate
        };
    };

    const [showFilters, setShowFilters] = useState(true);
    const [filterState, setFilterState] = useState<AlertEventsSearchFormState>(getFilterStateFromSearchFilters(searchFilters));

    useEffect(() => {
        setFilterState(getFilterStateFromSearchFilters(searchFilters));
    }, [searchFilters]);

    const clearFilterState = () => {
        setFilterState(getFilterStateFromSearchFilters(initialAlertEventsState.searchFilters));
    };

    const toggleShowFilters = () => {
        setShowFilters(!showFilters);
    };

    const handleSubmit = (event: FormEvent) => {
        event.preventDefault();
        const companyIds = mapSelectOptionsToValues(filterState.selectedCompanyOptions);
        const alertIds = mapSelectOptionsToValues(filterState.selectedAlertOptions);
        handleSearch({
            companyIds,
            alertIds,
            timeRangeType: filterState.selectedTimeRange.value as TimeRangeTypes,
            startDate: filterState.startDate,
            endDate: filterState.endDate,
        });
    };

    const handleUpdateCompanyOptions = (newValue: Array<SelectOption>) => {
        if (newValue) {
            setFilterState({
                ...filterState,
                selectedCompanyOptions: newValue
            });
        }
    };

    const handleUpdateAlertOptions = (newValue: Array<SelectOption>) => {
        if (newValue) {
            setFilterState({
                ...filterState,
                selectedAlertOptions: newValue
            });
        }
    };

    const handleTimeRangeChange = (newValue: SelectOption) => {
        if (newValue.value === TimeRangeTypes.CUSTOM) {
            setFilterState({
                ...filterState,
                selectedTimeRange: newValue
            });
        } else {
            setFilterState({
                ...filterState,
                selectedTimeRange: newValue,
                ...getTimeRange(newValue.value as TimeRangeTypes)
            });
        }
    };

    const handleStartDateChange = (newStartDate: Date) => {
        setFilterState({
            ...filterState,
            startDate: newStartDate
        });
    };

    const handleEndDateChange = (newEndDate: Date) => {
        setFilterState({
            ...filterState,
            endDate: newEndDate
        });
    };

    const companyOptions = useMemo(() => {
        return mapEntitiesWithNameAndIdToSelectOptions(companies).sort((a, b) => sortStrings(a.label, b.label));
    }, [companies]);

    const alertOptions = useMemo(() => {
        return mapEntitiesWithNameAndIdToSelectOptions(alerts).sort((a, b) => sortStrings(a.label, b.label));
    }, [alerts]);

    const timeRangeTypes = [
        TimeRangeTypes.TODAY,
        TimeRangeTypes.PAST_THREE_DAYS,
        TimeRangeTypes.PAST_SEVEN_DAYS,
        TimeRangeTypes.PAST_FOURTEEN_DAYS,
        TimeRangeTypes.CUSTOM
    ];

    return (
        <Form className={styles.alertEventsSearchForm} onSubmit={handleSubmit}>
            <SearchFormHeaderContainer
                idPrefix={"alert-events"}
                showFilters={showFilters}
                showToggle={true}
                toggleShowFilters={toggleShowFilters}
            />
            {showFilters && (
                <>
                    <SearchFormFilterRow>
                        <MultiSelectFormGroup
                            label={resources.common.searchForm.filters.organizations.label}
                            ariaLabel={resources.common.searchForm.filters.organizations.label}
                            automationId={"alert-events-search-filters-organizations-select"}
                            controlId={"alert-events-search-filters-organizations-select"}
                            disabled={companies.length === 1}
                            isClearable={companies.length > 1}
                            isRequired={false}
                            isSearchable={true}
                            labelWidth={labelWidth}
                            onChangeMulti={handleUpdateCompanyOptions}
                            options={companyOptions}
                            selectedOptions={filterState.selectedCompanyOptions}
                            keepMenuOpenOnSelect={true}
                            layout={layout}
                        />
                        <MultiSelectFormGroup
                            label={resources.pages.alertEvents.searchForm.filters.alerts.label}
                            ariaLabel={resources.pages.alertEvents.searchForm.filters.alerts.label}
                            automationId={"alert-events-search-filters-alerts-select"}
                            controlId={"alert-events-search-filters-alerts-select"}
                            disabled={false}
                            isClearable={true}
                            isRequired={false}
                            isSearchable={true}
                            labelWidth={labelWidth}
                            onChangeMulti={handleUpdateAlertOptions}
                            options={alertOptions}
                            selectedOptions={filterState.selectedAlertOptions}
                            keepMenuOpenOnSelect={true}
                            layout={layout}
                        />
                    </SearchFormFilterRow>
                    <TimeRangeSearchFormFilterRow
                        dateFormat={MONTH_DAY_YEAR_INPUT_DATE_FORMAT}
                        endDate={filterState.endDate}
                        endDateLabel={resources.common.searchForm.filters.endDate.label}
                        endDateAriaLabel={resources.common.searchForm.filters.endDate.label}
                        startDate={filterState.startDate}
                        startDateLabel={resources.common.searchForm.filters.startDate.label}
                        startDateAriaLabel={resources.common.searchForm.filters.startDate.label}
                        idPrefix={"alert-events"}
                        labelWidth={labelWidth}
                        layout={layout}
                        minDate={minDate}
                        maxDate={maxDate}
                        onChangeEndDate={handleEndDateChange}
                        onChangeStartDate={handleStartDateChange}
                        onChangeTimeRange={handleTimeRangeChange}
                        selectedTimeRange={filterState.selectedTimeRange}
                        timeRangeTypes={timeRangeTypes}
                        showMonthDropdown={true}
                        showYearDropdown={true}
                        showMonthYearPicker={false}
                    />
                    <div className={styles.buttonDiv}>
                        <Button id="alert-event-search-clear-button" variant={"outline-secondary"} onClick={clearFilterState}>{resources.common.buttons.clear.label}</Button>
                        <ButtonWithSpinner
                            id="alert-event-search-submit-button"
                            disabled={requestInProgress}
                            hideSpinner={false}
                            variant="primary"
                            type={ButtonTypes.SUBMIT}
                            label={resources.common.buttons.search.label}
                        />
                    </div>
                </>
            )}
        </Form>
    );
};